/**
 * Vera Share route tree, mounted by the shell at /share.
 *
 * ProtectedRoute requires a Share.* role (Share.User for recipients; global
 * Admin passes). That is gate 1, the module gate: it limits who can see the
 * module exists at all. Gate 2, which files you can actually open, is a
 * data-level check in the BFF against share.file_recipient, and no UI state
 * here influences it.
 *
 * The Publish page is in the nav only for share.publish holders, but the BFF
 * is the enforcer: a user without it gets a clean 403 from the API.
 */

import { lazy, Suspense, createContext, useContext } from 'react';
import { Navigate, Outlet, type RouteObject } from 'react-router-dom';
import { Spinner } from '@fluentui/react-components';
import { CloudArrowUpRegular, ShareRegular } from '@fluentui/react-icons';
import { AppShell, type NavEntry } from '@widgets/app-shell';
const ModuleGuidePage = lazy(() =>
  import('@widgets/module-guide').then((m) => ({ default: m.ModuleGuidePage }))
);
import { ErrorBoundary } from '@shared/ui';
import { ProtectedRoute } from '@shared/auth';
import { useShareMeta } from '@share/api/useShare';

/** Dusk Blue: the module's locked accent (brand secondary, shared with Tools). */
export const SHARE_ACCENT = '#3B5F73';

const SharedWithMePage = lazy(() =>
  import('@share/pages/inbox').then((m) => ({ default: m.SharedWithMePage }))
);
const ShareViewPage = lazy(() =>
  import('@share/pages/view').then((m) => ({ default: m.ShareViewPage }))
);
const PublishListPage = lazy(() =>
  import('@share/pages/publish').then((m) => ({ default: m.PublishListPage }))
);

function LazyPage({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<Spinner style={{ padding: '2rem' }} label="Loading..." />}>
      {children}
    </Suspense>
  );
}

const ShareBasePathContext = createContext<string>('');

/** Base path the module is mounted at ('/share' in the shell). */
export function useShareBasePath(): string {
  return useContext(ShareBasePathContext);
}

function buildNavItems(basePath: string, canPublish: boolean): NavEntry[] {
  const p = (suffix: string) => `${basePath}${suffix}` || '/';
  return [
    { path: p('/'), label: 'Shared with me', icon: ShareRegular },
    ...(canPublish
      ? ([{ path: p('/publish'), label: 'Published by me', icon: CloudArrowUpRegular }] as NavEntry[])
      : []),
  ];
}

export function ShareRootLayout() {
  const basePath = useShareBasePath();
  const canPublish = useShareMeta().data?.me.canPublish ?? false;
  return (
    <ProtectedRoute appRolePrefix="Share">
      <ErrorBoundary>
        <AppShell
          appId="share"
          appTitle="Vera Share"
          headerColor={SHARE_ACCENT}
          navItems={buildNavItems(basePath, canPublish)}
        >
          <Outlet />
        </AppShell>
      </ErrorBoundary>
    </ProtectedRoute>
  );
}

export function shareRoutes(basePath: string = ''): RouteObject[] {
  return [
    {
      element: (
        <ShareBasePathContext.Provider value={basePath}>
          <ShareRootLayout />
        </ShareBasePathContext.Provider>
      ),
      children: [
        { index: true, element: <LazyPage><SharedWithMePage /></LazyPage> },
        { path: 'publish', element: <LazyPage><PublishListPage /></LazyPage> },
        { path: 'f/:slug', element: <LazyPage><ShareViewPage /></LazyPage> },
        // The module's whole documentation set, read here rather than in Academy
        // (Sasha, 2026-08-16). Content is authored in Academy and never copied.
        { path: 'guide', element: <LazyPage><ModuleGuidePage module="share" basePath={basePath} /></LazyPage> },
        { path: 'guide/:slug', element: <LazyPage><ModuleGuidePage module="share" basePath={basePath} /></LazyPage> },
        { path: '*', element: <Navigate to={basePath || '/'} replace /> },
      ],
    },
  ];
}
